import * as React from 'react';
import {useDispatch} from 'react-redux';

import classnames from 'classnames';

import {Routing} from 'nutshell-core/routing';

import {CloseButton} from 'shells/button/close-button/close-button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Modal} from 'shells/modal/modal';
import {QuoteOverviewHeader} from 'shells/sales-documents/quote-overview-header';
import {SalesDocumentModalFooter} from 'shells/sales-documents/sales-document-modal-footer';
import {StatusSection} from 'shells/sales-documents/status/status-section';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';

import {EmailModalWrapper} from '../../../core-entity/components/email-modal-wrapper';
import {QUOTES} from '../../../reports/report-constants';
import type {ReportKey, ChartReportParams} from '../../../reports/report-types';
import {requestLeadsReportData} from '../../../reports/reports-actions';
import {
    getReportFiltersFromUrlParams,
    getReportParamsFromUrlParams,
} from '../../../setup/sales-documents/shared/helper';

import {useDismissibleUserSetting} from '../../../../util/hooks/use-dismissible-user-setting';

import {refetchForEvent} from '../../refetch-helpers';

import {useGetInvoices} from '../invoices/graphql/hooks';
import {
    QuoteStatusOnboarding,
    downloadQuote,
    getQuoteTimes,
    handleSalesDocumentChange,
} from '../shared';
import {PEANUT_TARGET, HAS_DISMISSED_STATUS_FOLLOWUP} from '../shared/consts';
import {
    useUpdateSalesDocumentStatus,
    useDuplicateSalesDocument,
    useRenderPeanut,
} from '../shared/graphql/hooks';

import {useGetQuoteById, useGetQuotes, useCreateInvoiceFromQuote} from './graphql/hooks';
import {QuoteWrapper} from './quote-wrapper';

import styles from '../shared/quotes.module.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    quoteId: string;
};

export function QuoteModal(props: Props) {
    const [hasActiveSection, setHasActiveSection] = React.useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
    const [isStatusFollowupOpen, setIsStatusFollowupOpen] = React.useState<boolean>(false);
    const [isEmailModalBeingOpened, setIsEmailModalBeingOpened] = React.useState<boolean>(false);

    const refetchLoading = React.useRef<boolean>(false);
    const peanutTargetRef = React.useRef<HTMLElement | null>(null);

    const {quote, isLoading, error} = useGetQuoteById(props.quoteId);

    const quoteLeadId = quote && quote.lead ? quote.lead.id : '';

    const {refetch} = useGetQuotes(quoteLeadId, isLoading || !quoteLeadId);
    const {refetch: refetchInvoices} = useGetInvoices(quoteLeadId, isLoading || !quoteLeadId);

    const {updateSalesDocumentStatus} = useUpdateSalesDocumentStatus();
    const {duplicateSalesDocument} = useDuplicateSalesDocument();
    const {createInvoiceFromQuote} = useCreateInvoiceFromQuote();

    const {shouldShow: shouldShowStatusFollowup, onDismiss: onDismissStatusFollowup} =
        useDismissibleUserSetting(HAS_DISMISSED_STATUS_FOLLOWUP);

    const isQuoteArchived = quote && quote.displayStatus === 'ARCHIVED';
    const instanceId = NutshellFrontendSettings.instanceId;
    const {rerender: rerenderPeanut} = useRenderPeanut(instanceId, quote, peanutTargetRef.current);

    const handleDuplicateQuote = () => {
        duplicateSalesDocument({
            salesDocument: props.quoteId,
        }).then((response) => {
            const newQuote =
                response &&
                response.data &&
                response.data.salesDocumentDuplicate &&
                response.data.salesDocumentDuplicate.salesDocument;
            if (newQuote) {
                handleSalesDocumentChange(newQuote.modalUrl, refetchLoading, refetch);
            }
        });
    };

    const handleUpdateStatus = (status: QuoteDisplayStatus) => {
        updateSalesDocumentStatus({
            id: props.quoteId,
            status,
        }).then(() => {
            if (status === 'ARCHIVED') {
                refetch();
            }

            if (status === 'ACCEPTED' || status === 'SENT') {
                window.dispatchEvent(new Event('onboarding-step-completed'));
            }

            // Re-render peanut when the status is updated
            rerenderPeanut();

            if (NutClientConfig.hasLeadPageRedesignM3FF) {
                refetchForEvent('salesDocumentStatusChange');
            } else {
                // Keeps lead in sync in the background if a status change
                // affected a stage goal
                $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);

                Timeline.refreshTimeline();
            }
        });
        setHasActiveSection(false);
    };

    const handleToggleActiveSection = (isActive: boolean) => {
        setHasActiveSection(isActive);
    };

    const handleBeforeClose = React.useCallback(() => {
        if (hasActiveSection && !isQuoteArchived) {
            setShowConfirmation(true);

            return false;
        }

        return true;
    }, [hasActiveSection, isQuoteArchived]);

    const handleClose = () => {
        // Refetch report data when modal is closed so report reflects changes without a page refresh
        fetchReportData(QUOTES, reportParams, reportFilters);

        if (hasActiveSection) {
            setShowConfirmation(true);
        } else {
            props.onClose();
        }
    };

    const handleConfirmClose = () => {
        setShowConfirmation(false);
        setHasActiveSection(false);
        props.onClose();
    };

    const handleCancelConfirmation = (e: Event) => {
        e.stopPropagation();
        setShowConfirmation(false);
    };

    const handleQuoteDownload = (slug: string, scriptRef: HTMLElement, anchor: string) => {
        if (!instanceId) {
            return;
        }

        downloadQuote(instanceId, slug, scriptRef, anchor);
    };

    const handleSendActionMenuClose = () => {
        if (shouldShowStatusFollowup) {
            setIsStatusFollowupOpen(true);
        }
    };

    const handleSendViaEmail = () => {
        setIsEmailModalBeingOpened(true);
    };

    const handlePostEmailModalOpen = () => {
        setIsEmailModalBeingOpened(false);
        handleClose();
    };

    const handleCreateInvoiceFromQuote = () => {
        createInvoiceFromQuote({
            quote: props.quoteId,
        }).then((response) => {
            const invoiceRes =
                response &&
                response.data &&
                response.data.invoiceCreateFromQuote &&
                response.data.invoiceCreateFromQuote.invoice;
            if (invoiceRes) {
                handleSalesDocumentChange(invoiceRes.modalUrl, refetchLoading, refetchInvoices);
                window.dispatchEvent(new Event('onboarding-step-completed'));
            }
        });
    };

    const urlParamString = window.location.search.slice(1); // Remove "?"
    const urlParams = Routing.deparam(urlParamString);

    const reportFilters = getReportFiltersFromUrlParams(urlParams);
    const reportParams = getReportParamsFromUrlParams(urlParams);

    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportKeyForFetch: ReportKey, params: ChartReportParams, filters: Array<any>) => {
            dispatch(
                requestLeadsReportData({
                    reportKey: reportKeyForFetch,
                    reportParams: params,
                    filters,
                })
            );
        },
        [dispatch]
    );

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                onClose={handleClose}
                onBeforeClose={handleBeforeClose}
                // @ts-expect-error
                maxHeight='90vh'
                minWidth={800}
                isWide={true}
                noPadding={true}
                closeButton={false}
                visibleOverflow={true}
            >
                {isLoading && (
                    <div className='absolutely-center-and-fill'>
                        <LoadingPage />
                    </div>
                )}
                {!isLoading && (error || !quote) && (
                    <div className='absolutely-center-and-fill'>
                        <ErrorState errorMessage={error} />
                    </div>
                )}
                {!isLoading && !error && quote && (
                    <>
                        <div className={styles['close-button-container']}>
                            <CloseButton onClick={handleClose} size={16} />
                        </div>
                        <QuoteOverviewHeader
                            quote={quote}
                            handleQuoteDownload={handleQuoteDownload}
                            handleUpdateStatus={handleUpdateStatus}
                            handleDuplicateQuote={handleDuplicateQuote}
                            handleSendActionMenuClose={handleSendActionMenuClose}
                            handleSendEmail={handleSendViaEmail}
                            handleCreateInvoiceFromQuote={handleCreateInvoiceFromQuote}
                        />
                        <div className={styles['quote-contents-container']}>
                            <div
                                className={classnames(
                                    quote.displayStatus === 'DRAFT' ? 'border-bottom' : '',
                                    styles['status-section-container']
                                )}
                            >
                                <StatusSection
                                    quoteStatus={quote.displayStatus}
                                    handleUpdateStatus={handleUpdateStatus}
                                    featureGated={
                                        !NutClientConfig.hasQuotePlan &&
                                        quote.displayStatus === 'DRAFT'
                                    }
                                    quoteTimes={getQuoteTimes(quote)}
                                    type='Quote'
                                />
                            </div>
                            {quote.displayStatus === 'DRAFT' ? (
                                <QuoteWrapper
                                    quote={quote}
                                    isTemplate={false}
                                    toggleHasActiveSection={handleToggleActiveSection}
                                />
                            ) : (
                                <div
                                    id={PEANUT_TARGET}
                                    // @ts-expect-error
                                    ref={peanutTargetRef}
                                    className={styles['peanut-embed']}
                                />
                            )}
                        </div>
                        <SalesDocumentModalFooter statusLog={quote.statusLog} />
                    </>
                )}
            </Modal>
            {props.isOpen && showConfirmation && !isQuoteArchived && (
                <ConfirmationDialog
                    title='Unsaved Changes'
                    message='Are you sure you want to close this quote without saving?'
                    onConfirm={handleConfirmClose}
                    // @ts-expect-error
                    onCancel={handleCancelConfirmation}
                    isOpen={showConfirmation}
                />
            )}
            {shouldShowStatusFollowup && onDismissStatusFollowup && (
                <QuoteStatusOnboarding
                    isStatusFollowupOpen={isStatusFollowupOpen}
                    setIsStatusFollowupOpen={setIsStatusFollowupOpen}
                    onDismissStatusFollowup={onDismissStatusFollowup}
                    handleUpdateStatus={handleUpdateStatus}
                    type='Quote'
                />
            )}
            {isEmailModalBeingOpened && quoteLeadId && (
                <EmailModalWrapper
                    entityId={quoteLeadId}
                    postModalOpen={handlePostEmailModalOpen}
                    originSalesDocumentType='Quote'
                />
            )}
        </>
    );
}
